import { pageForPath } from './access-page-rules.contract.js';
import { GAME_OPEN_TO_ALL_EMPLOYEES } from './game.contract.js';
// /exam/take + /exam/result go with /exam/my: they are the exam that "My exams" opens.
const ALWAYS_ALLOWED = new Set(['/', '/profile', '/leave', '/exam/my', '/exam/take', '/exam/result']);
export function underPrefix(pathname, prefixes) {
    return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
/** Whether a per-page rule blocks `pathname` for this profile. */
export function isPageBlocked(pathname, access) {
    const page = pageForPath(pathname);
    return !!page && access.pageBlocks.includes(page);
}
/** Whether a per-page rule grants `pathname` to this profile. */
export function isPageGranted(pathname, access) {
    const page = pageForPath(pathname);
    return !!page && access.pageGrants.includes(page);
}
export function canOpenPage(pathname, access) {
    if (isPageBlocked(pathname, access))
        return false;
    if (isPageGranted(pathname, access))
        return true;
    // Supernova Connect is checked before the app-wide admin shortcut: its admin is a named eCode
    // list, and during the trial its players are a chosen group — neither is "admin".
    if (underPrefix(pathname, ['/game/admin']))
        return access.isGameOperator;
    // The gifts' hand-over tab: the Lead HR, whom the shop's bell and DM send here (an app admin or the game admin is not one).
    if (underPrefix(pathname, ['/game/handout']))
        return access.isHrLead;
    if (underPrefix(pathname, ['/game']))
        return access.isGameOperator || (GAME_OPEN_TO_ALL_EMPLOYEES ? access.isEmployee : access.isGamePlayer);
    // Lead MO: MONET+LEAD or the named eCode list — before the admin shortcut, because every other
    // admin must 403 here too (user decision).
    if (pathname === '/monetization/mo-lead')
        return access.canMoLead;
    if (access.isAdmin)
        return true;
    if (ALWAYS_ALLOWED.has(pathname))
        return true;
    // A restricted dev-group viewer is pinned to the Developer report whatever its role flags say.
    if (access.isDevRestricted && pathname === '/developer')
        return true;
    if (pathname === '/marketing/competitor')
        return false;
    // Admin by default, like Competitor: a search spends a shared, metered allowance, so nobody is
    // let in by role or department. Everyone else comes in through a per-page grant (checked above).
    // `access.canCreativeSpy` is derived from this very predicate, so it must not be read here.
    if (pathname === '/marketing/creative-spy')
        return false;
    // Campaign Editor: a named list (app.GadsEditorAccess), because it moves real money. Creative Spy
    // had one too until it moved onto the page rules above.
    if (pathname === '/marketing/campaign-editor')
        return access.canGadsEditor;
    // App Spy keeps the audience it had as a report tab on /marketing, /monetization and /developer, and
    // whoever may use the block requests, which start from a search here and live in a tab of this page:
    // the flag is enough on its own, so a delegate account does not depend on also being monetOnly.
    if (pathname === '/marketing/app-spy') {
        return access.isUa || access.isMonet || access.monetOnly || access.isDev || access.hasDevApps || access.canAdmobBlock;
    }
    // Localize: UA only, not the DA that the general /marketing prefix below also lets in.
    if (pathname === '/marketing/localize')
        return access.canLocalize;
    if (underPrefix(pathname, ['/marketing']))
        return access.isUa;
    if (pathname === '/monetization/app-assignment')
        return access.canMonetAssign;
    if (underPrefix(pathname, ['/monetization']))
        return access.isMonet || access.monetOnly;
    if (pathname === '/developer/ads-decode')
        return false;
    // Encrypt tool is DEV-department only; per-app assigned outsiders keep reports only.
    if (pathname === '/developer/encrypt')
        return access.isDev;
    if (underPrefix(pathname, ['/developer']))
        return access.isDev || access.hasDevApps;
    if (pathname === '/growth/aso-translate')
        return access.isAso;
    if (pathname === '/growth/firebase-rc')
        return access.isMonet || access.monetOnly;
    if (pathname === '/growth/event-analytics')
        return access.isMonet || access.monetOnly;
    if (pathname === '/growth/ua-bot-console')
        return false;
    // Site content = all HR (lead + staff) + CMO; HR Admin below = HR LEAD only.
    if (pathname === '/growth/site-content')
        return access.isCmo || access.isHr;
    // The handbook is for staff, not partners; the API refuses anyone outside the directory anyway.
    if (pathname === '/handbook' || pathname === '/handbook/start' || pathname === '/travel-expense')
        return access.isEmployee;
    // Team Building vote: the page its notifications link to. Employees only; the API decides which
    // events each one sees (their roster), so there is nothing finer to gate here.
    if (pathname === '/team-vote')
        return access.isEmployee;
    // OKR dashboard admin: the roles that may enter KR targets and check-ins — never every employee.
    if (pathname === '/okr/admin')
        return access.isCto || access.isCmo || access.isHrLead || access.isDesignLead || access.isMonetLead;
    // Old path of the Team Building tab, kept only as a redirect — admin only, before the /hr rule.
    if (pathname === '/hr/team-building')
        return false;
    if (pathname === '/employees' || underPrefix(pathname, ['/hr']))
        return access.isHrLead;
    // Quiz: employees only — the socket handshake refuses anyone without an active Employee row.
    if (underPrefix(pathname, ['/quiz']))
        return access.isEmployee;
    // Issues + Docs: any internal employee may enter; project membership decides what they see.
    if (underPrefix(pathname, ['/issues', '/docs']))
        return access.isEmployee;
    // admin | CMO | CTO | COO | department lead, from the API's own read gate. Before the /finance
    // deny so the rest of Finance (dividend distribution) stays admin-only.
    if (pathname === '/finance/commission')
        return access.canReadCommission;
    // Store machines (the Play report collectors): SPN004, who holds their keys, and the CTO, who may download the
    // payment receipts there (user decision 28/10/2026). The API checks the same predicate; the receipts have a
    // narrower check of their own (admin or CTO).
    if (pathname === '/admin/store-machines')
        return access.canToggleNav || access.isCto;
    // /admin: SPN004 (canToggleNav) may open it for the Sidebar tab without app-wide admin.
    if (pathname === '/admin')
        return access.canToggleNav;
    if (underPrefix(pathname, ['/products', '/admin', '/finance', '/exam/admin']))
        return false;
    return false;
}
//# sourceMappingURL=nav-page-access.contract.js.map